import type { BetDetails } from 'src/types/domain/BetDetails'

export const betStatusDTOMap = {
  WON: 'won',
  LOST: 'lost',
  PENDING: 'pending',
  VOIDED: 'voided',
  CASHED_OUT: 'cashed_out'
} as const

export type BetStatusDTO = (typeof betStatusDTOMap)[keyof typeof betStatusDTOMap]

export interface SocialPlayerDTO {
  id: string
  displayName: string
  country: string
}

export interface BalanceDTO {
  amount: number
  currency: string
  playerId: string
}

export type BetDetailsDTO = BetDetails

export interface BetRejectedDTO {
  id: string
  playerId: string
  gameId: string
  roundId: string
  currency: string
  receivedAt: string
  amount: number
  details: BetDetailsDTO
}

export interface SocialBetDTO {
  bet: {
    id: string
    display_name: string
    country?: string
    game_id: string
    round_id: string
    amount: number
    details: BetDetailsDTO
    status: BetStatusDTO
    potential_payout: number
    created_at: string
  }
  social: {
    round_id: string
    total_bets: number
    total_amount: number
  }
}

/** Per-bet social record from Mnesia (camelCase, used in round:snapshot and bet:won/bet:cashed_out) */
export interface SocialBetSnapshotItemDTO {
  betId: string
  gameId: string
  roundId: string
  displayName?: string
  country?: string
  amount: number
  status: string
  cashoutMultiplier?: number | null
  actualPayout?: number | null
  /**
   * USD minor units. Optional because a server older than the release that
   * added it omits the key entirely — treat absence as "unknown", never as 0.
   */
  potentialPayout?: number | null
  details?: BetDetailsDTO | null
  createdAt?: string | null
}

/** Payload for the round:snapshot event pushed on social channel join */
export interface RoundSnapshotDTO {
  bets: SocialBetSnapshotItemDTO[]
}

export interface BetDTO {
  id: string
  playerId: string
  gameId: string
  roundId: string
  amount: number
  details: BetDetailsDTO
  status: BetStatusDTO
  potentialPayout: number
  currency: string
  createdAt: string
  // Crash game fields (optional)
  cashoutMultiplier?: number
  actualPayout?: number
}

export interface BetResponseDTO {
  balance: BalanceDTO
  bet: BetDTO
}

export interface PlaceBetRequestedDTO {
  id: string
  gameId: string
  roundId: string
  playerId: string
  sessionId: string
  amount: number
  currency: string
  details: BetDetails
  correlationId: string
  autoCashoutMultiplier?: number // Crash game: auto-cashout at this multiplier
}

export interface CalculatePayoutDTO {
  gameId: string
  roundId?: string // Optional: when provided, lightning round boost is applied if applicable
  betAmount: number
  betDetails: BetDetails
}

export interface PlayerDTO {
  id: string
  token: string
}

export interface TokenGameConfigDTO {
  lobbyDisabled: boolean
  externalLobbyUrl?: string
  externalDepositUrl?: string
  platform: 'MOBILE' | 'DESKTOP'
  language?: string
  country?: string
}

export interface GameConfigDTO {
  usdRate?: number
  bettingMode?: 'classic' | 'chip-placement'
  markets?: Array<{
    id: string
    type: 'main' | 'side_bet'
    label: string
    description?: string
    probabilities: Record<string, number>
    limits: { PickWinner: { min: number; max: number } }
    maxMultiplier: number
    options: Array<{
      id: string
      name: string
      color: string
      image?: string
      multiplier: number
      bettable: boolean
    }>
    displayOrder: number
  }>
  bet: {
    currency: string
    probabilities: Record<string, number>
    maxBetAmount: number
    minBetAmount: number
    defaultBetMultiplier?: number | null
    chipMultipliers?: number[] | null
    maxMultiplier: number
    maxPayout: number
    limits: {
      PickWinner: {
        min: number
        max: number
      }
      ReverseForecas?: {
        min: number
        max: number
      }
      StraightForecast?: {
        min: number
        max: number
      }
      CombinationTricast?: {
        min: number
        max: number
      }
      Tricast?: {
        min: number
        max: number
      }
    }
  }
}
